import * as JSZipModule from "jszip";
import { create } from "zustand";
import {
  type DownloadProgressInfo,
  downloadObjectStorage,
} from "@/services/game";
import blobCacheManager from "@/utils/blobCacheManager";

export enum FileDownloadStatus {
  PENDING = "pending",
  DOWNLOADING = "downloading",
  DONE = "done",
  FAILED = "failed",
}
export enum DownloadTaskStatus {
  DOWNLOADING = "downloading",
  ZIPPING = "zipping",
  DONE = "done",
  FAILED = "failed",
}
export type DownloadTaskType = "file" | "folder";
type CustomDownloadOptions = {
  taskName: string;
  fileName: string;
  loader: (context?: {
    onDownloadProgress?: (progress: {
      loaded: number;
      total: number;
      lengthComputable: boolean;
    }) => void;
  }) => Promise<Blob | { blob: Blob; fileName?: string }>;
};
type RetryTaskPayload =
  | { kind: "file"; file: Pick<DownloadNode, "key" | "name"> }
  | { kind: "folder"; folder: DownloadNode }
  | { kind: "custom"; options: CustomDownloadOptions };

export type FileProgressItem = {
  key: string;
  name: string;
  status: FileDownloadStatus;
  percent: number;
  speedBps: number;
  loadedBytes: number;
  totalBytes: number;
};

export type DownloadNode = {
  key: string;
  name: string;
  isDir: boolean;
  children?: DownloadNode[];
};

export type DownloadTask = {
  id: string;
  name: string;
  type: DownloadTaskType;
  status: DownloadTaskStatus;
  progress: number;
  zipProgress: number;
  currentDownloadSpeedBps: number;
  totalFiles: number;
  downloadedFiles: number;
  fileProgressList: FileProgressItem[];
  expanded: boolean;
  createdAt: number;
  updatedAt: number;
  downloadStartedAt: number;
  zipStartedAt: number;
  retryPayload?: RetryTaskPayload;
};

type GlobalDownloadState = {
  tasks: DownloadTask[];
  getCachedOrFetchBlob: (
    key: string,
    onProgress?: (progress: DownloadProgressInfo) => void,
  ) => Promise<Blob>;
  enqueueFileDownload: (
    file: Pick<DownloadNode, "key" | "name">,
  ) => Promise<void>;
  enqueueCustomDownload: (options: {
    taskName: string;
    fileName: string;
    loader: (context?: {
      onDownloadProgress?: (progress: {
        loaded: number;
        total: number;
        lengthComputable: boolean;
      }) => void;
    }) => Promise<Blob | { blob: Blob; fileName?: string }>;
  }) => Promise<void>;
  enqueueFolderDownload: (folder: DownloadNode) => Promise<void>;
  retryTask: (taskId: string) => Promise<void>;
  toggleTaskExpanded: (taskId: string) => void;
  removeTask: (taskId: string) => void;
  clearCompletedTasks: () => void;
};

type SpeedRef = {
  loaded: number;
  ts: number;
  smoothedBps: number;
};

const getFileNameFromKey = (key: string) => {
  const trimmed = key.replace(/\/+$/, "");
  const parts = trimmed.split("/");
  return parts[parts.length - 1] || "download";
};

const createZipInstance = () => {
  const defaultExport = (JSZipModule as { default?: new () => any }).default;
  const JSZipCtor = defaultExport ?? (JSZipModule as unknown as new () => any);
  return new JSZipCtor();
};

const triggerBrowserDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const runWithConcurrency = async (
  tasks: Array<() => Promise<void>>,
  maxConcurrency: number,
) => {
  let index = 0;
  const workers = Array.from(
    { length: Math.min(maxConcurrency, tasks.length) },
    async () => {
      while (index < tasks.length) {
        const taskIndex = index;
        index += 1;
        await tasks[taskIndex]();
      }
    },
  );
  await Promise.all(workers);
};

const collectFilesFromNode = (node: DownloadNode): DownloadNode[] => {
  if (!node.isDir) {
    return [node];
  }
  const files: DownloadNode[] = [];
  const walk = (children?: DownloadNode[]) => {
    if (!children || children.length === 0) {
      return;
    }
    children.forEach((child) => {
      if (child.isDir) {
        walk(child.children);
      } else {
        files.push(child);
      }
    });
  };
  walk(node.children);
  return files;
};

const makeTaskId = () =>
  `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const getOverallPercent = (
  status: DownloadTaskStatus,
  fileProgressList: FileProgressItem[],
  totalFiles: number,
) => {
  const totalCount = fileProgressList.length || totalFiles;
  if (totalCount <= 0) {
    return status === DownloadTaskStatus.ZIPPING ? 100 : 0;
  }
  const doneCount = fileProgressList.filter(
    (item) => item.status === FileDownloadStatus.DONE,
  ).length;
  const raw = Math.round((doneCount / totalCount) * 100);
  if (status === DownloadTaskStatus.DOWNLOADING && doneCount < totalCount) {
    return Math.min(raw, 99);
  }
  return raw;
};

const getCurrentSpeed = (fileProgressList: FileProgressItem[]) =>
  fileProgressList.reduce((sum, item) => {
    if (item.status === FileDownloadStatus.DOWNLOADING) {
      return sum + item.speedBps;
    }
    return sum;
  }, 0);

const MAX_TASKS = 30;
const FOLDER_DOWNLOAD_CONCURRENCY = 5;
const SPEED_SAMPLE_MIN_MS = 300;
const SPEED_SMOOTHING_ALPHA = 0.3;

const createSpeedRef = (loaded = 0): SpeedRef => ({
  loaded,
  ts: Date.now(),
  smoothedBps: 0,
});

const computeSmoothedSpeed = (
  speedRef: SpeedRef,
  loaded: number,
  now: number,
) => {
  if (loaded < speedRef.loaded) {
    speedRef.loaded = loaded;
    speedRef.ts = now;
    speedRef.smoothedBps = 0;
    return 0;
  }

  const deltaLoaded = loaded - speedRef.loaded;
  const deltaMs = now - speedRef.ts;

  if (deltaLoaded <= 0) {
    speedRef.loaded = loaded;
    speedRef.ts = now;
    return speedRef.smoothedBps;
  }

  // For the first valid sample, return immediate speed so UI can show it quickly.
  if (deltaMs > 0 && speedRef.smoothedBps <= 0) {
    const instantBps = (deltaLoaded * 1000) / deltaMs;
    if (Number.isFinite(instantBps) && instantBps >= 0) {
      speedRef.smoothedBps = instantBps;
      speedRef.loaded = loaded;
      speedRef.ts = now;
      return speedRef.smoothedBps;
    }
  }

  // Keep accumulating bytes/time before reaching sample window for smoothing.
  if (deltaMs < SPEED_SAMPLE_MIN_MS) {
    return speedRef.smoothedBps;
  }

  const instantBps = (deltaLoaded * 1000) / deltaMs;
  if (!Number.isFinite(instantBps) || instantBps < 0) {
    return speedRef.smoothedBps;
  }

  speedRef.smoothedBps =
    speedRef.smoothedBps > 0
      ? speedRef.smoothedBps * (1 - SPEED_SMOOTHING_ALPHA) +
        instantBps * SPEED_SMOOTHING_ALPHA
      : instantBps;
  speedRef.loaded = loaded;
  speedRef.ts = now;
  return speedRef.smoothedBps;
};

const updateTaskList = (
  taskList: DownloadTask[],
  taskId: string,
  updater: (task: DownloadTask) => DownloadTask,
) => taskList.map((task) => (task.id === taskId ? updater(task) : task));

const createInitialTask = (
  id: string,
  name: string,
  type: DownloadTaskType,
  retryPayload?: RetryTaskPayload,
): DownloadTask => {
  const now = Date.now();
  return {
    id,
    name,
    type,
    status: DownloadTaskStatus.DOWNLOADING,
    progress: 0,
    zipProgress: 0,
    currentDownloadSpeedBps: 0,
    totalFiles: 0,
    downloadedFiles: 0,
    fileProgressList: [],
    expanded: false,
    createdAt: now,
    updatedAt: now,
    downloadStartedAt: now,
    zipStartedAt: 0,
    retryPayload,
  };
};

const fetchBlobWithCache = async (
  key: string,
  onProgress?: (progress: DownloadProgressInfo) => void,
) => {
  const cachedBlob = blobCacheManager.getBlob(key);
  if (cachedBlob) {
    onProgress?.({
      percent: 100,
      loaded: cachedBlob.size,
      total: cachedBlob.size,
      lengthComputable: true,
    });
    return cachedBlob;
  }
  const blob = await downloadObjectStorage(key, onProgress);
  blobCacheManager.setBlob(key, blob);
  return blob;
};

export const useGlobalDownloadStore = create<GlobalDownloadState>(
  (set, get) => ({
    tasks: [],
    getCachedOrFetchBlob: async (key, onProgress) =>
      fetchBlobWithCache(key, onProgress),
    enqueueFileDownload: async (file) => {
      const taskId = makeTaskId();
      const taskName = file.name || getFileNameFromKey(file.key);
      const speedRef = createSpeedRef();

      set((state) => ({
        tasks: [
          createInitialTask(taskId, taskName, "file", {
            kind: "file",
            file,
          }),
          ...state.tasks,
        ].slice(0, MAX_TASKS),
      }));

      try {
        const blob = await fetchBlobWithCache(file.key, (progress) => {
          const now = Date.now();
          const speedBps = computeSmoothedSpeed(speedRef, progress.loaded, now);
          set((state) => ({
            tasks: updateTaskList(state.tasks, taskId, (task) => ({
              ...task,
              progress: progress.percent,
              currentDownloadSpeedBps: Number.isFinite(speedBps) ? speedBps : 0,
              updatedAt: Date.now(),
            })),
          }));
        });

        triggerBrowserDownload(blob, taskName);
        set((state) => ({
          tasks: updateTaskList(state.tasks, taskId, (task) => ({
            ...task,
            status: DownloadTaskStatus.DONE,
            progress: 100,
            currentDownloadSpeedBps: 0,
            updatedAt: Date.now(),
          })),
        }));
      } catch (_error) {
        set((state) => ({
          tasks: updateTaskList(state.tasks, taskId, (task) => ({
            ...task,
            status: DownloadTaskStatus.FAILED,
            currentDownloadSpeedBps: 0,
            updatedAt: Date.now(),
          })),
        }));
      }
    },
    enqueueCustomDownload: async ({ taskName, fileName, loader }) => {
      const taskId = makeTaskId();
      const speedRef = createSpeedRef();
      const customOptions: CustomDownloadOptions = {
        taskName,
        fileName,
        loader,
      };
      set((state) => ({
        tasks: [
          createInitialTask(taskId, taskName || fileName, "file", {
            kind: "custom",
            options: customOptions,
          }),
          ...state.tasks,
        ].slice(0, MAX_TASKS),
      }));
      try {
        const loaderResult = await loader({
          onDownloadProgress: (progress) => {
            const now = Date.now();
            const speedBps = computeSmoothedSpeed(
              speedRef,
              progress.loaded,
              now,
            );
            set((state) => ({
              tasks: updateTaskList(state.tasks, taskId, (task) => ({
                ...task,
                progress: progress.lengthComputable
                  ? Math.min(
                      99,
                      Math.round(
                        (progress.loaded / (progress.total || 1)) * 100,
                      ),
                    )
                  : task.progress,
                currentDownloadSpeedBps: Number.isFinite(speedBps)
                  ? speedBps
                  : 0,
                updatedAt: Date.now(),
              })),
            }));
          },
        });
        const resolvedBlob =
          loaderResult instanceof Blob ? loaderResult : loaderResult.blob;
        const resolvedFileName =
          loaderResult instanceof Blob
            ? fileName
            : loaderResult.fileName || fileName;
        triggerBrowserDownload(resolvedBlob, resolvedFileName);
        set((state) => ({
          tasks: updateTaskList(state.tasks, taskId, (task) => ({
            ...task,
            status: DownloadTaskStatus.DONE,
            progress: 100,
            currentDownloadSpeedBps: 0,
            updatedAt: Date.now(),
          })),
        }));
      } catch (_error) {
        set((state) => ({
          tasks: updateTaskList(state.tasks, taskId, (task) => ({
            ...task,
            status: DownloadTaskStatus.FAILED,
            currentDownloadSpeedBps: 0,
            updatedAt: Date.now(),
          })),
        }));
        throw _error;
      }
    },
    enqueueFolderDownload: async (folder) => {
      const taskId = makeTaskId();
      const taskName = folder.name || getFileNameFromKey(folder.key);
      const fileList = collectFilesFromNode(folder);
      const dirPrefix = `${folder.key.replace(/\/+$/, "")}/`;
      const preparedFiles: Array<{ relativePath: string; blob: Blob } | null> =
        new Array(fileList.length).fill(null);
      const fileSpeedRef = new Map<string, SpeedRef>();

      const initialTask = createInitialTask(taskId, taskName, "folder", {
        kind: "folder",
        folder,
      });
      initialTask.totalFiles = fileList.length;
      initialTask.fileProgressList = fileList.map((item) => ({
        key: item.key,
        name: item.name || getFileNameFromKey(item.key),
        status: FileDownloadStatus.PENDING,
        percent: 0,
        speedBps: 0,
        loadedBytes: 0,
        totalBytes: 0,
      }));

      set((state) => ({
        tasks: [initialTask, ...state.tasks].slice(0, MAX_TASKS),
      }));

      try {
        let downloadedSuccessCount = 0;
        let hasFailedTask = false;
        const tasks = fileList.map((file, taskIndex) => async () => {
          try {
            const blob = await fetchBlobWithCache(file.key, (progress) => {
              const now = Date.now();
              const speedRef =
                fileSpeedRef.get(file.key) ?? createSpeedRef(progress.loaded);
              if (!fileSpeedRef.has(file.key)) {
                fileSpeedRef.set(file.key, speedRef);
              }
              const speedBps = computeSmoothedSpeed(
                speedRef,
                progress.loaded,
                now,
              );

              set((state) => ({
                tasks: updateTaskList(state.tasks, taskId, (task) => {
                  const nextFileProgressList = task.fileProgressList.map(
                    (item) =>
                      item.key === file.key
                        ? {
                            ...item,
                            percent: progress.percent,
                            speedBps: Number.isFinite(speedBps) ? speedBps : 0,
                            loadedBytes: progress.loaded,
                            totalBytes: progress.total,
                          }
                        : item,
                  );
                  return {
                    ...task,
                    fileProgressList: nextFileProgressList,
                    progress: getOverallPercent(
                      task.status,
                      nextFileProgressList,
                      task.totalFiles,
                    ),
                    currentDownloadSpeedBps:
                      getCurrentSpeed(nextFileProgressList),
                    updatedAt: Date.now(),
                  };
                }),
              }));
            });

            const relativePath = file.key.startsWith(dirPrefix)
              ? file.key.slice(dirPrefix.length)
              : file.name || getFileNameFromKey(file.key);
            if (relativePath) {
              preparedFiles[taskIndex] = { relativePath, blob };
            }

            downloadedSuccessCount += 1;
            set((state) => ({
              tasks: updateTaskList(state.tasks, taskId, (task) => {
                const nextFileProgressList = task.fileProgressList.map(
                  (item) =>
                    item.key === file.key
                      ? {
                          ...item,
                          status: FileDownloadStatus.DONE,
                          percent: 100,
                          speedBps: 0,
                          loadedBytes: blob.size,
                          totalBytes: blob.size,
                        }
                      : item,
                );
                return {
                  ...task,
                  downloadedFiles: downloadedSuccessCount,
                  fileProgressList: nextFileProgressList,
                  progress: getOverallPercent(
                    task.status,
                    nextFileProgressList,
                    task.totalFiles,
                  ),
                  currentDownloadSpeedBps:
                    getCurrentSpeed(nextFileProgressList),
                  updatedAt: Date.now(),
                };
              }),
            }));
          } catch (_error) {
            hasFailedTask = true;
            set((state) => ({
              tasks: updateTaskList(state.tasks, taskId, (task) => {
                const nextFileProgressList = task.fileProgressList.map(
                  (item) =>
                    item.key === file.key
                      ? {
                          ...item,
                          status: FileDownloadStatus.FAILED,
                          speedBps: 0,
                        }
                      : item,
                );
                return {
                  ...task,
                  fileProgressList: nextFileProgressList,
                  progress: getOverallPercent(
                    task.status,
                    nextFileProgressList,
                    task.totalFiles,
                  ),
                  currentDownloadSpeedBps:
                    getCurrentSpeed(nextFileProgressList),
                  updatedAt: Date.now(),
                };
              }),
            }));
          }
        });

        await runWithConcurrency(tasks, FOLDER_DOWNLOAD_CONCURRENCY);

        if (hasFailedTask) {
          throw new Error("folder download failed");
        }

        set((state) => ({
          tasks: updateTaskList(state.tasks, taskId, (task) => ({
            ...task,
            status: DownloadTaskStatus.ZIPPING,
            progress: 100,
            zipProgress: 0,
            currentDownloadSpeedBps: 0,
            zipStartedAt: Date.now(),
            updatedAt: Date.now(),
          })),
        }));

        const zip = createZipInstance();
        for (const preparedFile of preparedFiles) {
          if (preparedFile) {
            zip.file(preparedFile.relativePath, preparedFile.blob);
          }
        }
        const zipBlob = await zip.generateAsync(
          { type: "blob" },
          (metadata: { percent: number }) => {
            set((state) => ({
              tasks: updateTaskList(state.tasks, taskId, (task) => ({
                ...task,
                zipProgress: Math.max(0, Math.min(100, metadata.percent)),
                updatedAt: Date.now(),
              })),
            }));
          },
        );

        const zipName = `${taskName.replace(/\.zip$/i, "")}.zip`;
        triggerBrowserDownload(zipBlob, zipName);
        set((state) => ({
          tasks: updateTaskList(state.tasks, taskId, (task) => ({
            ...task,
            status: DownloadTaskStatus.DONE,
            zipProgress: 100,
            updatedAt: Date.now(),
          })),
        }));
      } catch (_error) {
        set((state) => ({
          tasks: updateTaskList(state.tasks, taskId, (task) => ({
            ...task,
            status: DownloadTaskStatus.FAILED,
            currentDownloadSpeedBps: 0,
            updatedAt: Date.now(),
          })),
        }));
      }
    },
    retryTask: async (taskId) => {
      const task = get().tasks.find((item) => item.id === taskId);
      if (!task?.retryPayload) {
        return;
      }
      set((state) => ({
        tasks: state.tasks.filter((item) => item.id !== taskId),
      }));
      try {
        if (task.retryPayload.kind === "file") {
          await get().enqueueFileDownload(task.retryPayload.file);
          return;
        }
        if (task.retryPayload.kind === "folder") {
          await get().enqueueFolderDownload(task.retryPayload.folder);
          return;
        }
        await get().enqueueCustomDownload(task.retryPayload.options);
      } catch (_error) {
        // Ignore retry errors to keep download center interaction non-blocking.
      }
    },
    toggleTaskExpanded: (taskId) => {
      set((state) => ({
        tasks: updateTaskList(state.tasks, taskId, (task) => ({
          ...task,
          expanded: !task.expanded,
        })),
      }));
    },
    removeTask: (taskId) => {
      set((state) => ({
        tasks: state.tasks.filter((task) => task.id !== taskId),
      }));
    },
    clearCompletedTasks: () => {
      const tasks = get().tasks;
      set({
        tasks: tasks.filter(
          (task) =>
            task.status !== DownloadTaskStatus.DONE &&
            task.status !== DownloadTaskStatus.FAILED,
        ),
      });
    },
  }),
);
